import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import ReactDOM from 'react-dom'
import { Box, styled, useTheme, Typography, Backdrop, Button } from '@mui/material'
import { socket, recordingSocket } from '../../../config/SocketApi';
import Peer from 'simple-peer';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../auth/hooks/useAuthContext';
import StudentMeeting from './StudentMeeting';
import { useNavigate } from 'react-router-dom';
import { getIceConfig } from '../../../config/iceConfig';
import { SOCKET_EVENTS } from '../../../config/socketEvents';
import { safeSignalPeer } from '../helpers/realtimeUtils';

// STYLED COMPONENTS - MOVED OUTSIDE to prevent recreation on every render
const AskedForMicWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.text.secondary}`,
    borderRadius: '16px',
    margin: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    padding: 16,
    maxWidth: '85vw',
    width: '280px',
    '@media (max-width: 768px)': {
        padding: 12,
        width: '250px',
        '& .MuiTypography-h4': {
            fontSize: '1rem',
            textAlign: 'center',
        },
    },
}));

const ButtonsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column-reverse', // Accept button on top
    width: '100%',
    margin: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    backgroundColor: theme.palette.background.default,
    gap: 10,
    '& .MuiButton-root': {
        width: '100%',
    },
}));

const VideoBox = styled(Box)(({ showCamera, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    flex: showCamera ? 6 : undefined,
    padding: showCamera ? '8px' : 0,
    '@media (max-width: 768px)': {
        flex: showCamera ? 1 : undefined,
        padding: showCamera ? '4px' : 0,
        minWidth: showCamera ? '150px' : undefined,
    },
}));

const StudentVideoStream = ({ isMobileOverlay = false }) => {
    const navigate = useNavigate();
    const [tutorStream, setTutorStream] = useState();

    const [attendees, setAttendees] = useState([])
    const [meetingStarted, setMeetingStarted] = useState(false)
    const [tutorCamEnabled, setTutorCamEnabled] = useState(false)
    const [askedForMic, setAskedForMic] = useState(false)
    const theme = useTheme();
    const { user } = useAuthContext() || JSON.parse(localStorage.getItem("user"));
    const { t } = useTranslation();
    
    // Log component mount for debugging
    useEffect(() => {
        console.log("[StudentVideoStream] *** COMPONENT MOUNTED ***");
        console.log("[StudentVideoStream] Socket connected:", socket.connected);
        console.log("[StudentVideoStream] Socket ID:", socket.id);
        console.log("[StudentVideoStream] User:", user?.id, user?.full_name);
        
        return () => {
            console.log("[StudentVideoStream] *** COMPONENT UNMOUNTING ***");
        };
    }, []);
    
    // Calculate camera box dimensions - make it larger for visibility
    const cameraBoxWidth = Math.max(250, window.innerWidth * 5 / 25 - 6);
    const cameraBoxHeight = Math.max(160, cameraBoxWidth / 1.55);
    
    // Check if the stream has video tracks
    const hasVideoTrack = tutorStream && tutorStream.getVideoTracks().length > 0;
    
    // Show camera box only when:
    // 1. We have a stream WITH video tracks
    // 2. Tutor's camera is enabled (via socket event)
    // 3. Not in meeting mode
    const showCamera = hasVideoTrack && tutorCamEnabled && !meetingStarted;
    
    // Log stream info for debugging
    useEffect(() => {
        if (tutorStream) {
            console.log("[StudentVideoStream] Stream updated:", {
                streamId: tutorStream.id,
                active: tutorStream.active,
                videoTracks: tutorStream.getVideoTracks().length,
                audioTracks: tutorStream.getAudioTracks().length,
                tutorCamEnabled,
                showCamera
            });
        }
    }, [tutorStream, tutorCamEnabled, showCamera]);
    
    const studentVideoRef = useRef()
    const camerBoxRef = useRef()
    const studentPeerRef = useRef();
    const senderStudentsPeerRefs = useRef([]);
    const studentsPeerRefs = useRef([])
    const attendeesRef = useRef([])
    const serverHandlerPeer = useRef()
    const isReceivingCameraStream = useRef(false); // Track if we're already receiving camera stream

    const requestTutorVideoStream = useCallback((reason = 'ready') => {
        if (!socket.connected) return;
        socket.emit('student-ready-for-video-stream', { reason });
    }, []);

    useEffect(() => {
        requestTutorVideoStream('mount');

        const handleConnect = () => requestTutorVideoStream('connect');
        const handleOrientationChange = () => {
            setTimeout(() => requestTutorVideoStream('orientation-change'), 250);
        };

        socket.on('connect', handleConnect);
        window.addEventListener('orientationchange', handleOrientationChange);
        window.visualViewport?.addEventListener('resize', handleOrientationChange);

        return () => {
            socket.off('connect', handleConnect);
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.visualViewport?.removeEventListener('resize', handleOrientationChange);
        };
    }, [requestTutorVideoStream]);

    useEffect(() => {
        // socket.emit("student-connected", {})
        socket.on(SOCKET_EVENTS.LEGACY_TUTOR_SIGNAL, (data) => {
            console.log("[StudentVideoStream] Received totur-signal:", {
                signalType: data?.signal?.type,
                tutorId: data?.tutor_id,
                hasSignal: !!data?.signal,
            });
            
            if (data.signal.type !== 'offer') {
                // This is an ICE candidate or answer - forward to existing peer
                if (studentPeerRef.current && !studentPeerRef.current.destroyed) {
                    safeSignalPeer(studentPeerRef.current, data.signal, "StudentVideoStream tutor-answer");
                } else {
                    console.log("[StudentVideoStream] WARNING: No peer to signal for non-offer signal");
                }
            } else {
                // This is an offer - ALWAYS create new peer for renegotiation
                // The tutor may have switched stream type (video->audio or audio->video)
                console.log("[StudentVideoStream] Received new offer, creating new peer");
                
                // Destroy any existing peer before creating new one
                if (studentPeerRef.current) {
                    console.log("[StudentVideoStream] Destroying existing peer for renegotiation");
                    try {
                        studentPeerRef.current.destroy();
                    } catch (e) {
                        console.log("[StudentVideoStream] Error destroying peer:", e.message);
                    }
                    studentPeerRef.current = null;
                }
                
                isReceivingCameraStream.current = true;
                
                const iceConfig = getIceConfig();
                const newPeer = new Peer({
                    initiator: false,
                    trickle: true,
                    config: {
                        ...iceConfig,
                        iceTransportPolicy: 'relay'  // Force TURN relay for better connectivity
                    }
                });
                safeSignalPeer(newPeer, data.signal, "StudentVideoStream tutor-offer")
                newPeer.on("stream", (stream) => {
                    const videoTracks = stream.getVideoTracks();
                    const audioTracks = stream.getAudioTracks();
                    
                    console.log("[StudentVideoStream] *** STREAM RECEIVED FROM TUTOR ***");
                    console.log("[StudentVideoStream] Stream details:", {
                        streamId: stream.id,
                        streamActive: stream.active,
                        videoTracks: videoTracks.length,
                        audioTracks: audioTracks.length,
                    });
                    
                    if (videoTracks.length > 0) {
                        const vt = videoTracks[0];
                        
                        // Get track settings to see actual dimensions
                        const settings = vt.getSettings();
                        console.log("[StudentVideoStream] Video track details:", {
                            id: vt.id,
                            label: vt.label,
                            kind: vt.kind,
                            enabled: vt.enabled,
                            muted: vt.muted,
                            readyState: vt.readyState,
                            contentHint: vt.contentHint,
                        });
                        console.log("[StudentVideoStream] Video track settings:", {
                            width: settings.width,
                            height: settings.height,
                            frameRate: settings.frameRate,
                            aspectRatio: settings.aspectRatio,
                        });
                        
                        if (!settings.width || !settings.height) {
                            console.warn("[StudentVideoStream] WARNING: Video track has no dimensions!");
                        }
                        
                        // Add track event listeners
                        vt.onended = () => console.log("[StudentVideoStream] Video track ended!");
                        vt.onmute = () => console.log("[StudentVideoStream] Video track muted!");
                        vt.onunmute = () => console.log("[StudentVideoStream] Video track unmuted!");
                        
                        // Force enable the track
                        if (!vt.enabled) {
                            console.log("[StudentVideoStream] Enabling disabled video track");
                            vt.enabled = true;
                        }
                        
                        setTutorCamEnabled(true);
                    } else { 
                        console.log("[StudentVideoStream] Audio-only stream received (no video tracks)");
                        setTutorCamEnabled(false);
                    }
                    
                    if (audioTracks.length > 0) {
                        const at = audioTracks[0];
                        console.log("[StudentVideoStream] Audio track details:", {
                            id: at.id,
                            label: at.label,
                            enabled: at.enabled,
                            muted: at.muted,
                            readyState: at.readyState,
                        });
                        
                        // Force enable audio
                        if (!at.enabled) {
                            console.log("[StudentVideoStream] Enabling disabled audio track");
                            at.enabled = true;
                        }
                    } else {
                        console.warn("[StudentVideoStream] WARNING: No audio tracks in stream!");
                    }
                    
                    setTutorStream(stream);
                })

                newPeer.on("signal", (signal) => {
                    console.log("[StudentVideoStream] Sending student-signal to tutor:", data.tutor_id, "type:", signal.type);
                    socket.emit(SOCKET_EVENTS.STUDENT_SIGNAL, { signal, tutor_id: data.tutor_id });

                });
                newPeer.on('error', (err) => { 
                    console.log("[StudentVideoStream] Peer error:", err.message);
                    isReceivingCameraStream.current = false;
                })
                newPeer.on('connect', () => { 
                    console.log("[StudentVideoStream] Peer connected successfully");
                })
                newPeer.on('close', () => {
                    console.log("[StudentVideoStream] Peer closed");
                    isReceivingCameraStream.current = false;
                })
                studentPeerRef.current = newPeer;
            }
        })
        socket.on("student-disconnect", (socket_id) => {
            const disconnectdSenderStudent = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === socket_id);
            if (disconnectdSenderStudent) {
                disconnectdSenderStudent?.peer.destroy();
                senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== socket_id);
            }
        })

        socket.on("user-disconnected-meeting", (student) => {
            if (student) {
                attendeesRef.current = attendeesRef.current.filter(a => a.socket_id !== student.socket_id)
                setAttendees(attendeesRef.current)
            }

        })

        socket.on("close-mic", (socket_id) => {
            if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
            studentsPeerRefs.current = [];
            attendeesRef.current.map(s => {
                if (s.socket_id === socket_id) {
                    if (s.stream) {
                        s.stream.getTracks().forEach((track) => {
                            track.stop();
                        });
                        s.stream = null;
                    }
                }
            })
            setAttendees(attendeesRef.current)

        })
        socket.on("close-student-mic", (socket_id) => {
            if (senderStudentsPeerRefs.current.length) {
                const disconnectdSenderStudent = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === socket_id);
                if (disconnectdSenderStudent) {
                    disconnectdSenderStudent?.peer.destroy();
                    senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== socket_id);
                }
            }
            attendeesRef.current.map(s => {
                if (s.socket_id === socket_id) {
                    if (s.stream) {
                        s.stream.getTracks().forEach((track) => {
                            track.stop();
                        });
                        s.stream = null;
                    }
                }
            })
            setAttendees(attendeesRef.current)
        })

        socket.on("student-stream-signal", (data) => {
            if (data.signal.type === 'offer') {
                const iceConfig = getIceConfig();
                const newPeer = new Peer({
                    initiator: false,
                    trickle: true,
                    config: {
                        ...iceConfig,
                        iceTransportPolicy: 'relay'  // Force TURN relay for better connectivity
                    }
                });
                safeSignalPeer(newPeer, data.signal, "StudentVideoStream attendee-offer")
                newPeer.on("stream", (stream) => {
                    attendeesRef.current = attendeesRef.current.map(a => {
                        if (a.socket_id === data.sender_attendee_socket_id) {
                            a.stream = stream
                        }
                        return a;
                    })
                })
                newPeer.on("signal", (signal) => {
                    socket.emit("student-receiver-stream-signal", { signal, sender_attendee_socket_id: data.sender_attendee_socket_id, sender_server_handler_socket_id: data.sender_server_handler_socket_id, peer_socket_id: socket.id });
                });
                newPeer.on("connect", () => {
                    if (attendeesRef.current) setAttendees(attendeesRef.current)
                })
                newPeer.on('error', (err) => { console.log("the peer error is ", err) })
                const timeStamp = Date.now();
                if (senderStudentsPeerRefs.current.length) {
                    senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== data.sender_attendee_socket_id)
                    senderStudentsPeerRefs.current.push({ peer: newPeer, sender_student_socket_id: data.sender_attendee_socket_id, timeStamp });

                } else {
                    senderStudentsPeerRefs.current.push({ peer: newPeer, sender_student_socket_id: data.sender_attendee_socket_id });
                }
            } else {
                const studentSocketId = data.sender_attendee_socket_id;
                if (senderStudentsPeerRefs.current.length) {
                    const studentPeer = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === studentSocketId)
                    if (studentPeer) {
                        safeSignalPeer(studentPeer?.peer, data.signal, "StudentVideoStream attendee-answer");
                    }
                }
            }
        })

        socket.on("server-receiver-stream-to-student-signal", (data) => {
            const signal = data.data;
            if (serverHandlerPeer.current) {
                safeSignalPeer(serverHandlerPeer.current, signal, "StudentVideoStream server-handler");
            }
        })
        recordingSocket.on("end-session-student", () => {
            console.log("[StudentVideoStream] Received end-session-student on recordingSocket");
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (serverHandlerPeer.current) { serverHandlerPeer.current.destroy(); serverHandlerPeer.current = null }

            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                    a.stream = false;
                }
            })
            studentsPeerRefs.current = []
            senderStudentsPeerRefs.current = [];
            attendeesRef.current = []
            navigate("/dashboard")
        })
        // Also listen on main socket for end-session-student (fallback)
        socket.on("end-session-student", () => {
            console.log("[StudentVideoStream] Received end-session-student on main socket");
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (serverHandlerPeer.current) { serverHandlerPeer.current.destroy(); serverHandlerPeer.current = null }

            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                    a.stream = false;
                }
            })
            studentsPeerRefs.current = []
            senderStudentsPeerRefs.current = [];
            attendeesRef.current = []
            navigate("/dashboard")
        })
        socket.on("stop-video-recording-received", () => {
            if (studentPeerRef?.current !== null) {
                studentPeerRef.current.destroy();
                studentPeerRef.current = null;
            }
            // Clear the video ref AFTER stopping the stream
            if (studentVideoRef.current) {
                studentVideoRef.current.srcObject = null;
            }
            setTutorStream(null)
        })

        socket.on("open-mic", () => {
            console.log("[StudentVideoStream] Received open-mic event");
            setAskedForMic(true)

        })
        socket.on("tutor-cam-enabled", (condition) => {
            setTutorCamEnabled(condition)
        })
        socket.on("student-connected-meeting", student => {
            const foundUser = attendeesRef.current.find(a => a.user_id === student.user_id)
            if (foundUser) {
                attendeesRef.current = [...attendeesRef.current.filter(a => a.user_id !== student.user_id), student]
            } else {
                attendeesRef.current = [...attendeesRef.current, student]
            }
            setAttendees(attendeesRef.current)
        })
        socket.on("meeting-state-sent", (data) => {
            const dataParsed = JSON.parse(data);
            setMeetingStarted(dataParsed.state)
        })
        socket.on("meeting-end-session", () => {
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                }
            })
            attendeesRef.current = []
        })
        socket.on("connect_error", error => console.log(error))

        return () => {
            recordingSocket.off("end-session-student")
            socket.off("end-session-student");
            socket.off("meeting-state-sent");
            socket.off("meeting-end-session");
            socket.off("student-connected-meeting");
            socket.off("user-disconnected-meeting");
            socket.off("tutor-cam-enabled");
            socket.off("connect_error");
            socket.off("open-mic");
            socket.off("close-mic");
            socket.off("close-student-mic");
            socket.off("student-stream-signal")
            socket.off(SOCKET_EVENTS.LEGACY_TUTOR_SIGNAL);
            socket.off("student-disconnect");
            socket.off('stop-video-recording-received')
            socket.off('start-recording-received')
            if (studentPeerRef?.current) {
                studentPeerRef.current.destroy();
                studentPeerRef.current = null;
            }
            // Clear the srcObject before nullifying the ref
            if (studentVideoRef.current) {
                studentVideoRef.current.srcObject = null;
            }
            if (tutorStream) {
                tutorStream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (serverHandlerPeer.current) { serverHandlerPeer.current.destroy(); serverHandlerPeer.current = null }

            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                }
            })
            studentsPeerRefs.current = []
            senderStudentsPeerRefs.current = [];
            attendeesRef.current = []
            setTutorStream(null)
        }

    }, [])

    const createServerHandlerPeer = (serverHandler, stream, attendee) => {
        console.log("[StudentVideoStream] createServerHandlerPeer called");
        console.log("[StudentVideoStream] Stream tracks:", stream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState })));
        console.log("[StudentVideoStream] Server handler:", serverHandler.socket_id);
        console.log("[StudentVideoStream] Attendee:", attendee?.socket_id, attendee?.user_id);
        
        // Destroy existing peer if any
        if (serverHandlerPeer.current) {
            console.log("[StudentVideoStream] Destroying existing server handler peer");
            try {
                serverHandlerPeer.current.destroy();
            } catch (e) {
                console.log("[StudentVideoStream] Error destroying peer:", e.message);
            }
            serverHandlerPeer.current = null;
        }
        
        const iceConfig = getIceConfig();
        const newPeer = new Peer({
            initiator: true,
            trickle: true,
            stream,
            config: {
                ...iceConfig,
                // Force TURN relay for reliable connections
                iceTransportPolicy: 'relay'
            }
        });
        
        newPeer.on("signal", (data) => {
            console.log("[StudentVideoStream] Peer signal, type:", data.type);
            socket.emit("student-stream-signal-to-server-handler", { signal: data, handlerSocketId: serverHandler.socket_id, peerSocketId: socket.id, attendee });
        });
        newPeer.on("connect", () => {
            console.log("[StudentVideoStream] *** Peer connected to server-handler ***");
        });
        newPeer.on("error", (err) => {
            console.log("[StudentVideoStream] Peer error:", err.message);
        });
        newPeer.on("close", () => {
            console.log("[StudentVideoStream] Peer closed");
        });
        serverHandlerPeer.current = newPeer;
    }
    useEffect(() => {
        if (attendees.length && !meetingStarted) {
            attendees.map(s_a => {
                if (s_a.user_id !== user.id) {
                    const socket_id = s_a.socket_id;
                    const foundAudioEl = document.getElementById(socket_id);
                    if (foundAudioEl) {
                        foundAudioEl.srcObject = s_a.stream
                    } else {
                        var audioEl = document.createElement('audio');
                        audioEl.setAttribute("id", s_a.socket_id)
                        audioEl.controls = false;
                        audioEl.autoplay = true;
                        audioEl.srcObject = s_a.stream;
                        document.body.appendChild(audioEl)

                    }

                }
            })
        }
    }, [attendees, meetingStarted])

    useEffect(() => {
        // Guard against null refs
        if (!studentVideoRef.current || !tutorStream) {
            return;
        }
        
        const videoElement = studentVideoRef.current;
        
        console.log("[StudentVideoStream] Setting video srcObject:", {
            videoRefExists: !!videoElement,
            streamActive: tutorStream.active,
            streamId: tutorStream.id,
            videoTracks: tutorStream.getVideoTracks().length,
            audioTracks: tutorStream.getAudioTracks().length,
            tutorCamEnabled,
            meetingStarted
        });
        
        // Check if video tracks are live
        const videoTrack = tutorStream.getVideoTracks()[0];
        if (videoTrack) {
            console.log("[StudentVideoStream] Video track state:", {
                id: videoTrack.id,
                label: videoTrack.label,
                enabled: videoTrack.enabled,
                muted: videoTrack.muted,
                readyState: videoTrack.readyState,
                kind: videoTrack.kind
            });
            
            // Force enable the track if it's disabled
            if (!videoTrack.enabled) {
                console.log("[StudentVideoStream] Enabling disabled video track");
                videoTrack.enabled = true;
            }
        } else {
            console.warn("[StudentVideoStream] WARNING: No video track in stream!");
        }
        
        // Set the stream. Keep the video element muted so browser autoplay
        // policies do not block the first video frames; audio is played by the
        // dedicated audio element below.
        videoElement.srcObject = tutorStream;
        videoElement.defaultMuted = true;
        videoElement.muted = true;
        
        // Add event listeners with null checks
        const handleLoadedMetadata = () => {
            if (videoElement) {
                console.log("[StudentVideoStream] Video metadata loaded, dimensions:", 
                    videoElement.videoWidth, "x", videoElement.videoHeight);
                
                // If dimensions are 0, there might be an issue
                if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
                    console.warn("[StudentVideoStream] WARNING: Video dimensions are 0x0!");
                }
            }
        };
        
        const handlePlay = () => {
            console.log("[StudentVideoStream] Video started playing");
            if (videoElement) {
                console.log("[StudentVideoStream] Playing video dimensions:", 
                    videoElement.videoWidth, "x", videoElement.videoHeight);
            }
        };
        
        const handleError = (e) => {
            console.error("[StudentVideoStream] Video element error:", e);
        };
        
        const handleCanPlay = () => {
            console.log("[StudentVideoStream] Video can play, dimensions:", 
                videoElement?.videoWidth, "x", videoElement?.videoHeight);
        };
        
        videoElement.onloadedmetadata = handleLoadedMetadata;
        videoElement.onplay = handlePlay;
        videoElement.onerror = handleError;
        videoElement.oncanplay = handleCanPlay;
        
        // Try to play the video explicitly
        const playVideo = async () => {
            if (!videoElement) return;
            
            try {
                await videoElement.play();
                console.log("[StudentVideoStream] Video play started (muted)");
            } catch (err) {
                console.error("[StudentVideoStream] Video play error:", err.message);
            }
        };
        
        playVideo();
        
        // Cleanup
        return () => {
            if (videoElement) {
                videoElement.onloadedmetadata = null;
                videoElement.onplay = null;
                videoElement.onerror = null;
                videoElement.oncanplay = null;
            }
        };

    }, [tutorStream, tutorCamEnabled, meetingStarted])

    useEffect(() => {
        if (meetingStarted && attendees.length && user) {
            console.log("the user reading to receive stream", user.id)
            socket.emit("user-ready-to-receive-stream", user.id)
        }
        studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
        senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
        attendeesRef.current.map(a => {
            if (a.user_id === user.id && a.stream && a.user_type === 0) {
                a.stream.getTracks().forEach((track) => {
                    track.stop();
                });
                a.stream = null;
                socket.emit("close-student-mic", a.user_id)
            }
        })
        attendeesRef.current = []
        socket.emit("get-connected-students-include-sender", students => {
            if (students.length) {
                attendeesRef.current = students//[...attendeesRef.current.filter(s => s.socket_id !== senderAttendee.socket_id), senderAttendee]

                setAttendees(students)

            }
        })
    }, [meetingStarted, user])
    const handleStreamStudentAudio = (attendee) => {
        if (!attendee) {
            attendee = attendeesRef.current.find(a => a.user_id === user.id)
            socket.emit("student-mic-connected", { student_socket_id: attendee.socket_id })
        }
        if (attendee.stream) {
            if (attendee.camEnabled) {
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        a.stream.getAudioTracks()[0].enabled = !attendee.micEnabled;
                        a.micEnabled = !attendee.micEnabled
                    }
                    return a;
                })
                setAttendees(attendeesRef.current)
            } else {
                attendee.stream.getTracks().forEach((track) => {
                    track.stop();
                });
                socket.emit("kill-attendee-stream", attendee)
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        a.stream = false;
                        a.micEnabled = false
                    }
                    return a;
                })
                if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
                studentsPeerRefs.current = [];
                setAttendees(attendeesRef.current)
            }

        } else {
            const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
            const constraints = {
                video: false, // Fallback to true if any of the video constraints are not supported
                audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                    sampleRate: 22050,
                    sampleSize: 16,
                    channelCount: 1,
                } : true // Fallback to true if any of the audio constraints are not supported
            };
            navigator.mediaDevices.getUserMedia(constraints).then(str => {
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        attendee.stream = str;
                        a.micEnabled = true;
                        a.camEnabled = false;
                    }
                    return a;
                })
                setAttendees(attendeesRef.current)
                handleStreamToServer(attendee, str)

            }).catch(error => console.log(error))
        }
    }
    const handleStreamStudentCam = (attendee) => {
        if (attendee.stream) {
            if (attendee.camEnabled) {
                if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
                studentsPeerRefs.current = [];
                attendee.stream.getTracks().forEach((track) => {
                    track.stop();
                });
                const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                const constraints = {
                    video: false, // Fallback to true if any of the video constraints are not supported
                    audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                        sampleRate: 22050,
                        sampleSize: 16,
                        channelCount: 1,
                    } : true // Fallback to true if any of the audio constraints are not supported
                };
                navigator.mediaDevices.getUserMedia(constraints).then(str => {
                    attendeesRef.current = attendeesRef.current.map(a => {
                        if (a.user_id === attendee.user_id) {
                            attendee.stream = str;
                            a.micEnabled = true;
                            a.camEnabled = false;
                        }
                        return a;
                    })
                    setAttendees(attendeesRef.current)
                    handleStreamToServer(attendee, str)
                }).catch(error => console.log(error))

            } else {
                if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
                studentsPeerRefs.current = [];
                if (attendee.stream) {
                    attendee.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                }
                const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                const constraints = {
                    video: supportedConstraints.width && supportedConstraints.frameRate && supportedConstraints.aspectRatio ? {
                        width: { max: 360 },
                        frameRate: { max: 10 },
                        aspectRatio: { exact: 1.55 }
                    } : true, // Fallback to true if any of the video constraints are not supported
                    audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                        sampleRate: 22050,
                        sampleSize: 16,
                        channelCount: 1,
                    } : true // Fallback to true if any of the audio constraints are not supported
                };
                navigator.mediaDevices.getUserMedia(constraints).then(str => {
                    attendee.stream = str;
                    attendee.camEnabled = true;
                    attendee.micEnabled = true;
                    attendeesRef.current = attendeesRef.current.map(a => {
                        if (a.user_id === attendee.user_id) {
                            attendee.stream = str;
                            a.micEnabled = true;
                            a.camEnabled = true;
                        }
                        return a;
                    })
                    setAttendees(attendeesRef.current)
                    handleStreamToServer(attendee, str)
                }).catch(error => console.log(error))
            }

        } else {
            const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
            const constraints = {
                video: supportedConstraints.width && supportedConstraints.frameRate && supportedConstraints.aspectRatio ? {
                    width: { max: 360 },
                    frameRate: { max: 10 },
                    aspectRatio: { exact: 1.55 }
                } : true, // Fallback to true if any of the video constraints are not supported
                audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                    sampleRate: 22050,
                    sampleSize: 16,
                    channelCount: 1,
                } : true // Fallback to true if any of the audio constraints are not supported
            };
            navigator.mediaDevices.getUserMedia(constraints).then(str => {
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        a.stream = str;
                        a.micEnabled = true;
                        a.camEnabled = true;
                    }
                    console.log("a after modifieng", a)
                    return a;
                })
                setAttendees(attendeesRef.current)
                handleStreamToServer(attendee, str)


            }).catch(error => console.log(error))
        }
    }
    const handleStreamToServer = useCallback((attendee, str) => {
        console.log("[StudentVideoStream] handleStreamToServer called, stream tracks:", str.getTracks().length);
        socket.emit("get-server-handler", serverHandler => {
            if (serverHandler) {
                console.log("[StudentVideoStream] Got server handler:", serverHandler.socket_id);
                createServerHandlerPeer(serverHandler, str, attendee);
            } else {
                console.log("[StudentVideoStream] No server handler available!");
            }
        })
    }, [attendees])

    // Mobile overlay mode - just render the video AND the mic popup via Portal
    if (isMobileOverlay) {
        return (
            <>
                {/* Mic popup for mobile - MUST use Portal to render outside circular container */}
                {askedForMic && ReactDOM.createPortal(
                    <Backdrop
                        sx={{ 
                            color: "#fff", 
                            zIndex: 9999,
                            position: 'fixed',
                        }}
                        open={true}
                    >
                        <AskedForMicWrapper>
                            <Typography variant='h4'>{t('ask_for_mic')}</Typography>
                            <ButtonsRow>
                                <Button 
                                    onClick={() => { 
                                        socket.emit('student-aplogized-mic', user.id); 
                                        setAskedForMic(false); 
                                    }} 
                                    variant="contained" 
                                    sx={{ backgroundColor: theme.palette.danger?.main || '#ef4444' }}
                                >
                                    {t("apologize")}
                                </Button>
                                <Button 
                                    onClick={() => { 
                                        console.log("[StudentVideoStream] Mobile: Accept mic clicked");
                                        handleStreamStudentAudio(null); 
                                        setAskedForMic(false);
                                    }} 
                                    variant="contained"
                                >
                                    {t("accept")}
                                </Button>
                            </ButtonsRow>
                        </AskedForMicWrapper>
                    </Backdrop>,
                    document.body
                )}
                
                <Box sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    bgcolor: '#0a0a14',
                }}>
                    <video 
                        ref={studentVideoRef}
                        style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }} 
                        autoPlay
                        playsInline
                        muted
                    />
                    {!tutorCamEnabled && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(26, 26, 46, 0.9)',
                            color: '#fff',
                            fontSize: 10,
                            textAlign: 'center',
                        }}>
                            Camera Off
                        </Box>
                    )}
                </Box>
                
                {/* Audio element for mobile */}
                {tutorStream && tutorStream.getAudioTracks().length > 0 && (
                    <audio 
                        key={`mobile-audio-${tutorStream.id}`}
                        ref={(el) => {
                            if (el && tutorStream) {
                                el.srcObject = tutorStream;
                                el.play().catch(e => console.log("[StudentVideoStream] Mobile audio play error:", e.message));
                            }
                        }}
                        autoPlay
                        playsInline
                        style={{ display: 'none' }}
                    />
                )}
            </>
        );
    }

    return (
        <VideoBox showCamera={showCamera}>
            <StudentMeeting attendees={attendees} tutorStream={tutorStream} meetingStarted={meetingStarted} handleStreamStudentAudio={(attendee) => handleStreamStudentAudio(attendee)} handleStreamStudentCam={(attendee) => handleStreamStudentCam(attendee)} />
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={askedForMic}
            >
                <AskedForMicWrapper>
                    <Typography variant='h4'>{t('ask_for_mic')}</Typography>
                    <ButtonsRow>
                        <Button onClick={() => { socket.emit('student-aplogized-mic', user.id); setAskedForMic(false); }} variant="contained" sx={{ backgroundColor: theme.palette.danger.main }}>{t("apologize")}</Button>
                        <Button onClick={() => { handleStreamStudentAudio(null); setAskedForMic(false) }} variant="contained">{t("accept")}</Button>
                    </ButtonsRow>
                </AskedForMicWrapper>

            </Backdrop>
            {/* Camera Video Box - Modern glassmorphism styling */}
            <Box 
                ref={camerBoxRef}
                data-testid="camera-box"
                sx={{
                    display: showCamera ? 'block' : 'none',
                    background: 'linear-gradient(145deg, rgba(26, 26, 46, 0.9) 0%, rgba(15, 15, 26, 0.95) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '16px',
                    width: cameraBoxWidth,
                    height: cameraBoxHeight,
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
                        transform: 'translateY(-2px)',
                    },
                    // Responsive
                    '@media (max-width: 768px)': {
                        width: '100%',
                        maxWidth: '300px',
                        height: 'auto',
                        aspectRatio: '16/9',
                        borderRadius: '12px',
                    },
                }}
            >
                {/* Live indicator */}
                <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: tutorStream?.active ? 'rgba(239, 68, 68, 0.9)' : 'rgba(100, 100, 100, 0.9)',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    zIndex: 2,
                    backdropFilter: 'blur(4px)',
                }}>
                    <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        animation: tutorStream?.active ? 'pulse 1.5s infinite' : 'none',
                        '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                        },
                    }} />
                    <Typography sx={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        color: '#fff',
                        letterSpacing: '0.5px',
                    }}>
                        {tutorStream?.active ? 'LIVE' : 'OFFLINE'}
                    </Typography>
                </Box>
                
                <video 
                    id='live_stream_video' 
                    ref={studentVideoRef}
                    data-testid="camera-video"
                    style={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        backgroundColor: '#0a0a14',
                        display: 'block',
                        opacity: 1,
                    }} 
                    autoPlay
                    playsInline
                    muted
                />
                
                {/* Tutor name overlay */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    padding: '20px 12px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <Typography sx={{
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 500,
                    }}>
                        Teacher Camera
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        padding: '2px 8px',
                        borderRadius: '12px',
                    }}>
                        <Typography sx={{ 
                            fontSize: '10px', 
                            color: 'rgba(255,255,255,0.7)',
                        }}>
                            {tutorStream?.getVideoTracks().length || 0}v/{tutorStream?.getAudioTracks().length || 0}a
                        </Typography>
                    </Box>
                </Box>
            </Box>
            
            {/* Audio element for tutor stream - always render when we have audio tracks
                This handles both:
                1. Audio-only streams (no video)
                2. Video+audio streams (video element handles video, this handles audio)
            */}
            {tutorStream && tutorStream.getAudioTracks().length > 0 && (
                <audio 
                    key={`audio-${tutorStream.id}`}
                    ref={(el) => {
                        if (el && tutorStream) {
                            console.log("[StudentVideoStream] Setting audio element srcObject, stream:", tutorStream.id);
                            el.srcObject = tutorStream;
                            el.play().then(() => {
                                console.log("[StudentVideoStream] Audio playback started successfully");
                            }).catch(e => {
                                console.log("[StudentVideoStream] Audio play error:", e.message);
                                // Try again with user interaction
                            });
                        }
                    }}
                    autoPlay
                    playsInline
                    style={{ display: 'none', position: 'absolute' }}
                />
            )}
        </VideoBox>
    )
}


export default memo(StudentVideoStream)
